import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowRight, CreditCard, Download, MessageSquare, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fcfa, fdate } from "@/lib/format";
import { fileUrl, uploadFile } from "@/lib/upload";
import { EmptyState, Loader, StatusBadge } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { OP_STATUS } from "@/pages/member/Operations";
import { Pilotage } from "@/components/member/OpManage";
import { usePayMethods } from "@/components/member/Contribution";
import { GroupPurchasePanel, TripPanel } from "@/components/member/CommercePanels";
import { SignaturePad, Votes } from "@/components/member/LeadTools";
import { ImageGallery, ImagesEditor } from "@/components/member/OpImages";
import { useAuth, can } from "@/context/AuthContext";

export const INST = { attendu: ["Attendu", "info"], declare: ["Déclaré", "warning"], confirme_auto: ["Confirmé automatiquement", "success"], valide: ["Validé", "success"],
  rejete: ["Rejeté", "danger"], en_retard: ["En retard", "danger"], rembourse: ["Remboursé", "info"] };
export const InstBadge = ({ s }) => <StatusBadge status={s} label={INST[s]?.[0]} tone={INST[s]?.[1]} />;

export async function download(path, name) {
  const { data } = await api.get(path, { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(data);
  a.download = name;
  a.click();
}

function JoinDialog({ op, onClose, onDone }) {
  const [shares, setShares] = useState(1);
  const [ok, setOk] = useState(false);
  const [sigName, setSigName] = useState("");
  const [sigImg, setSigImg] = useState("");
  const amount = shares * op.share_amount;
  const fee = Math.round((amount * op.fee_percent) / 100);
  const join = async () => {
    try {
      const { data } = await api.post(`/operations/${op.id}/join`, { shares, accept_agreement: ok, signature_name: sigName, signature_image: sigImg });
      toast.success(data.status === "liste_attente" ? "Opération complète : vous êtes sur la liste d'attente." : "Adhésion enregistrée : réglez votre premier versement.");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="join-dialog">
        <DialogHeader><DialogTitle>Rejoindre « {op.title} »</DialogTitle></DialogHeader>
        <label className="block"><span className="mr-label">Nombre de parts {op.max_shares_per_member ? `(maximum ${op.max_shares_per_member})` : ""}</span>
          <input data-testid="join-shares-input" type="number" min={1} max={op.max_shares_per_member || undefined} className="mr-input" value={shares} onChange={(e) => setShares(Math.max(1, Number(e.target.value)))} /></label>
        <div className="border border-mr-border rounded-[3px] p-3 text-sm space-y-1 tabular" data-testid="join-breakdown">
          <p className="flex justify-between"><span>Ma part</span><strong>{fcfa(amount)}</strong></p>
          <p className="flex justify-between text-mr-text2"><span>Frais Monrezo ({op.fee_percent} %), payés séparément au club</span><span data-testid="join-fee">{fcfa(fee)}</span></p>
          {op.default_schedule.map((l) => <p key={l.rank} className="flex justify-between text-xs text-mr-muted"><span>{l.label} · {l.percent} %{l.trigger === "jalon" ? " · appelée à la validation d'un jalon" : ""}{l.due_date ? ` · ${fdate(l.due_date)}` : ""}</span><span>{fcfa(Math.floor((amount * l.percent) / 100))}</span></p>)}
        </div>
        <div className="max-h-40 overflow-y-auto text-xs text-mr-text2 border border-mr-border rounded-[3px] p-3 whitespace-pre-line" data-testid="join-agreement">{op.agreement || "Convention de l'opération."}</div>
        <label className="flex gap-2 items-start text-sm"><input type="checkbox" data-testid="join-accept-checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} />J'ai lu la convention et je la signe électroniquement (date, heure, appareil et adresse IP enregistrés).</label>
        <input data-testid="join-signature-name" className="mr-input" placeholder="Votre nom complet (tel qu'il figure sur votre profil)" value={sigName} onChange={(e) => setSigName(e.target.value)} />
        <SignaturePad onChange={setSigImg} />
        <button data-testid="join-confirm-button" className="mr-btn-primary w-full" disabled={!ok || sigName.trim().length < 3 || !sigImg} onClick={join}>Signer et confirmer mon adhésion</button>
      </DialogContent>
    </Dialog>
  );
}

function Declare({ op, inst, onDone }) {
  const [method, setMethod] = useState("virement");
  const [file, setFile] = useState(null);
  const [ref, setRef] = useState("");
  const send = async () => {
    try {
      const proof = await uploadFile("payment_proof", file);
      await api.post(`/operations/${op.id}/installments/${inst.id}/declare`, { method, proof_file_id: proof, reference: ref });
      toast.success("Paiement déclaré : en attente de validation par le trésorier.");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-2 pt-2 border-t border-mr-border" data-testid="op-declare-form">
      <select data-testid="op-declare-method" className="mr-input" value={method} onChange={(e) => setMethod(e.target.value)}>
        <option value="virement">Virement bancaire (avis joint)</option><option value="wave">Wave hors application</option>{op.cash_enabled && <option value="especes">Espèces (reçu signé)</option>}</select>
      <input data-testid="op-declare-reference" className="mr-input" placeholder="Référence" value={ref} onChange={(e) => setRef(e.target.value)} />
      <label className="mr-btn-secondary w-full cursor-pointer"><Upload size={15} /> {file ? file.name : "Joindre la preuve"}<input type="file" className="hidden" data-testid="op-declare-file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files[0])} /></label>
      <button data-testid="op-declare-submit" className="mr-btn-secondary w-full" disabled={!file} onClick={send}>Déclarer</button>
    </div>
  );
}

function MyPayments({ op }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  const methods = usePayMethods();
  const load = () => api.get(`/operations/${op.id}/my-installments`).then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const pay = async (i, provider) => {
    try {
      const { data } = await api.post(`/operations/${op.id}/installments/${i.id}/checkout`, { provider });
      window.location.href = data.checkout_url.startsWith("http") ? data.checkout_url : data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!items) return <Loader />;
  if (!items.length) return <p className="text-sm text-mr-text2">Aucune échéance pour vous dans cette opération.</p>;
  return (
    <div className="space-y-3">
      {op.receiving_account && <div className="mr-card p-4 text-sm" data-testid="op-receiving-account"><p className="text-xs uppercase tracking-wider text-mr-muted mb-1">Compte de réception</p><p>{op.receiving_account.holder}</p>
        <p className="text-mr-muted tabular">{Object.values(op.receiving_account.references || {}).join(" · ")}</p><p className="text-xs text-mr-warning mt-2">Ces coordonnées ne changent jamais par message. Vérifiez-les toujours ici.</p></div>}
      {items.map((i) => (
        <div key={i.id} className="mr-card p-4 space-y-2" data-testid="op-installment-row">
          <div className="flex justify-between gap-3"><p className="text-sm font-medium">{i.kind === "frais" ? "Frais Monrezo (au club)" : `Tranche ${i.rank}`} · <span className="tabular">{fcfa(i.amount)}</span></p><InstBadge s={i.status} /></div>
          <p className="text-xs text-mr-muted tabular">{i.called === false ? "Appelée à la validation du jalon prévu" : `Échéance ${fdate(i.due_date)}`}{i.receipt_number ? ` · Reçu ${i.receipt_number}` : ""}</p>
          {i.reject_reason && <p className="text-xs text-mr-danger">Motif du rejet : {i.reject_reason}</p>}
          {["attendu", "en_retard", "rejete"].includes(i.status) && i.called !== false && (i.kind === "frais" || i.rank === 1 || items.some((x) => x.kind === "part" && x.rank === 1 && ["valide", "confirme_auto"].includes(x.status))) && (
            <div className="flex flex-wrap gap-2">{methods.connectors.map((c) => <button key={c.name} data-testid={`op-pay-${c.name}-button`} className="mr-btn-primary px-3" onClick={() => pay(i, c.name)}><CreditCard size={15} /> Payer avec {c.label}</button>)}
              <button data-testid="op-declare-toggle" className="mr-btn-ghost px-3" onClick={() => setOpen(open === i.id ? null : i.id)}>{methods.connectors.length ? "J'ai payé autrement" : "J'ai payé par virement"}</button></div>)}
          {open === i.id && <Declare op={op} inst={i} onDone={() => { setOpen(null); load(); }} />}
        </div>))}
      <button data-testid="op-statement-button" className="mr-btn-ghost px-3" onClick={() => download(`/operations/${op.id}/statement/${op.my_commitment.user_id}.pdf`, "releve.pdf")}><Download size={15} /> Mon relevé (PDF)</button>
    </div>
  );
}

function Docs({ op }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/operations/${op.id}/documents`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [op.id]);
  if (!items) return <Loader />;
  if (!items.length) return <p className="text-sm text-mr-text2" data-testid="op-docs-empty">Aucun document accessible.</p>;
  return <ul className="space-y-2">{items.map((d) => <li key={d.id}><button className="mr-card p-4 w-full text-left text-sm" data-testid="op-doc-row" onClick={async () => window.open(await fileUrl(d.file_id), "_blank", "noopener")}>{d.filename}</button></li>)}</ul>;
}

function Members({ op }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/operations/${op.id}/members`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [op.id]);
  if (!items) return <Loader />;
  return <ul className="space-y-2">{items.map((m) => <li key={m.commitment_id} className="mr-card p-4 flex justify-between text-sm" data-testid="op-member-row"><span>{m.name}{m.shares ? ` · ${m.shares} part(s)` : ""}</span><StatusBadge status={m.status} label={{ adherent: "Adhérent confirmé", candidat: "1er versement attendu", liste_attente: "Liste d'attente", defaillant: "En défaut" }[m.status]} /></li>)}</ul>;
}

function Updates({ op }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/operations/${op.id}/updates`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [op.id]);
  if (!items) return <Loader />;
  return (
    <div className="space-y-3">
      {op.milestones.map((m) => <div key={m.id} className="mr-card p-4 flex justify-between text-sm" data-testid="op-milestone-row"><span>{m.title}{m.due_date ? ` · ${fdate(m.due_date)}` : ""}</span><StatusBadge status={m.status} label={m.status === "atteint" ? "Atteint" : "Prévu"} /></div>)}
      {items.map((u) => <div key={u.id} className="mr-card p-4" data-testid="op-update-row"><p className="font-medium text-sm">{u.title}</p><p className="text-sm text-mr-text2 mt-1">{u.description}</p><p className="text-xs text-mr-muted mt-2">{fdate(u.created_at)}</p></div>)}
      {!items.length && !op.milestones.length && <p className="text-sm text-mr-text2">Aucune mise à jour.</p>}
    </div>
  );
}

function Rating({ op, onDone }) {
  const [score, setScore] = useState(0);
  const [comment, setComment] = useState("");
  const send = async () => {
    try {
      await api.post(`/operations/${op.id}/rating`, { score, comment });
      toast.success("Merci pour votre évaluation");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mr-card border-mr-accent space-y-3" data-testid="op-rating-card">
      <p className="font-medium">Évaluation obligatoire : notez cette opération</p>
      <div className="flex gap-2">{[1, 2, 3, 4, 5].map((n) => <button key={n} data-testid={`op-rating-${n}`} onClick={() => setScore(n)} className={`w-10 h-10 rounded-full border ${score >= n ? "border-mr-accent text-mr-accent" : "border-mr-border"}`}>{n}</button>)}</div>
      <textarea data-testid="op-rating-comment" className="mr-input" placeholder="Commentaire" value={comment} onChange={(e) => setComment(e.target.value)} />
      <button data-testid="op-rating-submit" className="mr-btn-primary" disabled={!score} onClick={send}>Envoyer</button>
    </div>
  );
}

export default function OperationDetail() {
  const { me } = useAuth();
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [op, setOp] = useState(null);
  const [joining, setJoining] = useState(false);
  const load = () => api.get(`/operations/${id}`).then(({ data }) => setOp(data)).catch(() => setOp(false));
  useEffect(() => {
    load();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (op === null) return <Loader />;
  if (!op) return <EmptyState title="Opération introuvable" />;
  const roles = op.my_roles;
  const insider = roles.some((r) => ["porteur", "co_porteur", "tresorier", "adherent", "admin"].includes(r));
  const manager = roles.some((r) => ["porteur", "co_porteur", "tresorier", "admin"].includes(r));
  const mine = op.my_commitment;
  const tabs = [["infos", "Infos"], ...(insider ? [["discussion", "Discussion"]] : []), ["documents", "Documents"], ...(insider ? [["adherents", "Adhérents"]] : []),
    ...(mine && mine.status !== "retire" ? [["paiements", "Paiements"]] : []), ...(insider ? [["mises-a-jour", "Mises à jour"], ["votes", "Votes"]] : []), ...(manager ? [["pilotage", "Pilotage"]] : [])];
  const tab = params.get("tab") || "infos";
  const t = op.totals;
  return (
    <div className="mr-fade-up space-y-5" data-testid="operation-detail">
      <div>
        <div className="flex flex-wrap items-center gap-2"><StatusBadge status={op.status} label={OP_STATUS[op.status]} testId="operation-status" />{op.team.map((m) => <span key={m.user_id} className="text-xs text-mr-muted">{m.role === "tresorier" ? "Trésorier" : "Porteur"} : {m.name}</span>)}</div>
        <h1 className="font-display font-bold text-2xl tracking-tight mt-2" data-testid="operation-title">{op.title}</h1>
        {op.share_amount > 0 && <p className="text-sm text-mr-text2 tabular mt-1">{op.shares_total} parts de {fcfa(op.share_amount)} · frais Monrezo {op.fee_percent} %</p>}
      </div>
      {op.rating_due && <Rating op={op} onDone={load} />}
      {op.status === "publie" && !manager && !["achat_groupe", "voyage"].includes(op.type) && (!mine || ["retire", "refuse", "interesse"].includes(mine.status)) && <button data-testid="operation-join-button" className="mr-btn-primary w-full" onClick={() => setJoining(true)}>Rejoindre <ArrowRight size={16} /></button>}
      {mine && ["candidat", "liste_attente", "defaillant"].includes(mine.status) && <p className="text-sm text-mr-warning" data-testid="operation-my-status">{{ candidat: "Adhésion enregistrée : vous serez « adhérent confirmé » après validation du premier versement.", liste_attente: "Vous êtes sur la liste d'attente.", defaillant: "Retard de paiement : la règle de la convention a été appliquée. Vous gardez l'accès à vos paiements." }[mine.status]}</p>}
      <div className="flex gap-1 border-b border-mr-border overflow-x-auto">{tabs.map(([k, l]) => (
        <button key={k} data-testid={`operation-tab-${k}`} onClick={() => setParams({ tab: k })} className={`shrink-0 px-3 min-h-[44px] text-sm font-medium border-b-2 -mb-px ${tab === k ? "border-mr-accent text-mr-text" : "border-transparent text-mr-muted"}`}>{l}</button>))}</div>
      {tab === "infos" && (
        <div className="space-y-4">
          <ImageGallery ids={op.photo_file_ids} />
          {(manager || can(me, "operations:manage")) && <ImagesEditor ids={op.photo_file_ids} endpoint={`/operations/${op.id}/images`} onSaved={load} testId="operation-images-editor" />}
          {op.type === "achat_groupe" && <GroupPurchasePanel opId={op.id} status={op.status} />}
          {op.type === "voyage" && <TripPanel opId={op.id} status={op.status} />}
          {t && <div className="mr-card grid grid-cols-2 sm:grid-cols-4 gap-4 tabular" data-testid="operation-totals">{[["Objectif", t.target], ["Engagé", t.committed], ["Validé", t.validated], ["Dépenses validées", t.expenses]].map(([l, v]) => <div key={l}><p className="text-xs text-mr-muted">{l}</p><p className="font-semibold">{fcfa(v)}</p></div>)}
            <p className="col-span-full text-xs text-mr-muted">{t.shares_taken}/{t.shares_total} parts · {t.participants} adhérents confirmés{op.waiting_list ? ` · ${op.waiting_list} en liste d'attente` : ""}</p></div>}
          <div className="mr-card space-y-4 text-[15px] text-mr-text2">{Object.entries({ summary: "Résumé", description: "Description", sector: "Secteur", location: "Lieu", sharing: "Partage des résultats", calendar: "Calendrier", risks: "Principaux risques" }).filter(([k]) => op.sheet[k]).map(([k, l]) => (
            <div key={k}><p className="text-xs uppercase tracking-wider text-mr-muted mb-1">{l}</p><ReactMarkdown>{String(op.sheet[k])}</ReactMarkdown></div>))}</div>
          {op.thresholds?.min_shares > 0 && <p className="text-sm text-mr-text2" data-testid="operation-threshold">Seuil : {op.thresholds.min_shares} parts avant le {fdate(op.thresholds.deadline)}. Sinon : {op.thresholds.refund_rule}</p>}
        </div>)}
      {tab === "discussion" && (op.conversation_id ? <Link to={`/app/messages/${op.conversation_id}`} className="mr-btn-secondary w-full" data-testid="operation-discussion-link"><MessageSquare size={16} /> Ouvrir le groupe de l'opération</Link> : <p className="text-sm text-mr-text2">Le groupe s'ouvre à la publication.</p>)}
      {tab === "documents" && <Docs op={op} />}
      {tab === "adherents" && <Members op={op} />}
      {tab === "paiements" && mine && <MyPayments op={op} />}
      {tab === "mises-a-jour" && <Updates op={op} />}
      {tab === "votes" && <Votes op={op} manager={manager} />}
      {tab === "pilotage" && manager && <Pilotage op={op} reload={load} />}
      {joining && <JoinDialog op={op} onClose={() => setJoining(false)} onDone={() => { setJoining(false); load(); setParams({ tab: "paiements" }); }} />}
    </div>
  );
}
